import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense } from 'react';

import { Loading } from '#components/loading.js';
import { ResizableLayout } from '#components/resizable-layout/resizable-layout.js';
import { useTRPC } from '#src/trpc.js';
import { PackageReviewForRevision } from './package-review-for-revision.js';
import { toRevisionSearchTree } from './package-review-page.helpers.js';
import { RevisionSearch } from './revision-search.js';

export const Route = createFileRoute('/_authorization-gate/wizard/admin/package-review/{-$revisionId}')({
	head: () => ({ meta: [{ title: 'Package Review • Wizard Admin • Cadstrom' }] }),
	component: WizardAdminPackageReviewPage,
});

function WizardAdminPackageReviewPage() {
	const { revisionId } = Route.useParams();
	const navigate = useNavigate();
	const trpc = useTRPC();

	const {
		data: { projectPreviews },
	} = useSuspenseQuery(trpc.project.listWithRevisions.queryOptions({}));

	const {
		data: { organizations },
	} = useSuspenseQuery(
		trpc.organization.getBatch.queryOptions({
			ids: projectPreviews.map((projectPreview) => projectPreview.ownerId).filter((ownerId) => ownerId !== null),
		}),
	);

	const tree = toRevisionSearchTree(projectPreviews, organizations);

	return (
		<ResizableLayout split='vertical' initialProportions={['18rem', '1fr']}>
			<div className='flex min-h-0 grow basis-0 flex-col overflow-auto p-2'>
				<RevisionSearch
					organizations={tree}
					selectedRevisionId={revisionId ?? null}
					onSelectRevision={(selectedRevisionId) =>
						void navigate({
							to: '/wizard/admin/package-review/{-$revisionId}',
							params: { revisionId: selectedRevisionId },
						})
					}
				/>
			</div>
			<div className='flex min-h-0 grow basis-0 flex-col'>
				<Suspense fallback={<Loading />}>
					{revisionId === undefined ?
						<div className='grid h-full w-full place-items-center p-2'>
							<p className='text-gray-a8 italic'>Select a revision to view package review status</p>
						</div>
					:	<PackageReviewForRevision key={revisionId} revisionId={revisionId} />}
				</Suspense>
			</div>
		</ResizableLayout>
	);
}
